import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Artifact } from '../types';
import { ArtifactBlock } from './ArtifactBlock';
import type { ImageIndex } from '../lib/docImages';

// ── Types ────────────────────────────────────────────────────────────────────

interface DocNode {
  slug: string;
  title: string;
  path: string;
  kind?: string;
  emoji?: string | null;
  order?: number;
  children: DocNode[];
}

interface DocVariantTree {
  label: string;
  rootPath: string;
  homePath?: string | null;
  docsLandingPath?: string | null;
  root: DocNode;
}

interface DocsHierarchy {
  appName: string;
  variants: string[];
  trees: Record<string, DocVariantTree>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function findArtifactForPath(artifacts: Artifact[], nodePath: string): Artifact | undefined {
  const norm = nodePath.replace(/\\/g, '/');
  return (
    artifacts.find((a) => a.name.replace(/\\/g, '/') === norm) ??
    artifacts.find((a) => a.name.replace(/\\/g, '/').endsWith('/' + norm))
  );
}

function firstSelectablePath(tree: DocVariantTree): string | null {
  if (tree.docsLandingPath) return tree.docsLandingPath;
  if (tree.homePath) return tree.homePath;
  return tree.root?.children?.[0]?.path ?? null;
}

function flattenNodes(nodes: DocNode[], acc: DocNode[] = []): DocNode[] {
  for (const n of nodes) {
    acc.push(n);
    if (n.children?.length) flattenNodes(n.children, acc);
  }
  return acc;
}

// Doc-tree page artifacts live under a `cloud/` or `agc/` prefix and end in `.md`.
const DOC_PATH_RE = /^(cloud|agc)\/.+\.md$/;

function isDocTreeArtifact(a: Artifact): boolean {
  return DOC_PATH_RE.test(a.name.replace(/\\/g, '/'));
}

function titleFromSlug(slug: string): string {
  return slug
    .replace(/\.md$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleFromArtifact(a: Artifact, slug: string): string {
  const m = a.content.match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : titleFromSlug(slug);
}

const VARIANT_LABELS: Record<string, string> = {
  cloud: 'Cloud',
  agc: 'Government Cloud',
};

/**
 * Reconstruct a DocsHierarchy from the `cloud/`/`agc/` `.md` artifact paths when
 * no `docs-hierarchy.json` manifest is present. The manifest, when present, is
 * preferred because it carries explicit titles, ordering, and emoji; this is a
 * best-effort fallback so the tree still renders without it.
 */
function buildHierarchyFromArtifacts(artifacts: Artifact[]): DocsHierarchy | null {
  const docs = artifacts.filter(isDocTreeArtifact);
  if (!docs.length) return null;

  const variantsSet = new Set<string>();
  const trees: Record<string, DocVariantTree> = {};
  // Directory nodes keyed by their full accumulated path, so a leaf page and a
  // directory that happen to share a slug never get conflated.
  const dirByPath = new Map<string, DocNode>();

  for (const a of docs) {
    const name = a.name.replace(/\\/g, '/');
    const segments = name.split('/');
    const variant = segments[0];
    variantsSet.add(variant);

    let tree = trees[variant];
    if (!tree) {
      tree = {
        label: VARIANT_LABELS[variant] ?? variant.toUpperCase(),
        rootPath: variant,
        root: { slug: variant, title: variant, path: variant, children: [] },
      };
      trees[variant] = tree;
    }

    const rel = segments.slice(1); // path under the variant prefix
    const fileName = rel[rel.length - 1];
    const dirSegments = rel.slice(0, -1);

    // Walk/create directory nodes from the root down.
    let parent = tree.root;
    const accum: string[] = [variant];
    for (const seg of dirSegments) {
      accum.push(seg);
      const key = accum.join('/');
      let node = dirByPath.get(key);
      if (!node) {
        node = {
          slug: seg,
          title: titleFromSlug(seg),
          path: `${key}/index.md`,
          children: [],
        };
        dirByPath.set(key, node);
        parent.children.push(node);
      }
      parent = node;
    }

    if (fileName === 'index.md') {
      // This artifact is the page for its containing directory node.
      parent.title = titleFromArtifact(a, dirSegments[dirSegments.length - 1] ?? variant);
      parent.path = name;
    } else {
      // A leaf page within the parent directory.
      parent.children.push({
        slug: fileName.replace(/\.md$/, ''),
        title: titleFromArtifact(a, fileName),
        path: name,
        children: [],
      });
    }
  }

  // Cloud first, then AGC, then anything else alphabetically.
  const variants = [...variantsSet].sort((a, b) => {
    const rank = (v: string) => (v === 'cloud' ? 0 : v === 'agc' ? 1 : 2);
    return rank(a) - rank(b) || a.localeCompare(b);
  });

  return { appName: '', variants, trees };
}

// ── Public API ───────────────────────────────────────────────────────────────

export function hasDocHierarchy(artifacts: Artifact[]): boolean {
  return (
    artifacts.some((a) => a.name.endsWith('docs-hierarchy.json')) ||
    artifacts.some(isDocTreeArtifact)
  );
}

// ── Icons ────────────────────────────────────────────────────────────────────

function PageIcon({ color }: { color: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <rect x="2" y="1" width="10" height="12" rx="1.2" stroke={color} strokeWidth="1.2" />
      <path
        d="M4.5 4.5h5M4.5 7h5M4.5 9.5h3"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

interface DocHierarchyViewerProps {
  artifacts: Artifact[];
  /** Width of the left tree sidebar. Default 260 for the panel, pass 300 for fullscreen. */
  sidebarWidth?: number;
  /** Project-wide image index so a page's relative `<img>` refs render inline. */
  imageIndex?: ImageIndex;
}

export function DocHierarchyViewer({ artifacts, sidebarWidth = 260, imageIndex }: DocHierarchyViewerProps) {
  const hierarchy = useMemo<DocsHierarchy | null>(() => {
    const h = artifacts.find((a) => a.name.endsWith('docs-hierarchy.json'));
    if (h) {
      try {
        // Best-effort: appending '}' recovers only the simple case of a
        // missing closing brace; other truncation modes fall through to fallback.
        let attempt = h.content;
        for (let i = 0; i <= 5; i++) {
          try {
            const parsed = JSON.parse(attempt);
            if (parsed?.trees && parsed?.variants?.length) return parsed as DocsHierarchy;
            break;
          } catch {
            attempt += '}';
          }
        }
      } catch {
        // fall through to path-based reconstruction
      }
    }
    // No manifest (or it failed to parse) — rebuild the tree from artifact paths.
    return buildHierarchyFromArtifacts(artifacts);
  }, [artifacts]);

  const [activeVariant, setActiveVariant] = useState<string>(
    () => hierarchy?.variants[0] ?? 'cloud',
  );

  // If artifacts change and the selected variant is no longer in the new
  // hierarchy, reset to the first available variant.
  useEffect(() => {
    if (hierarchy && !hierarchy.variants.includes(activeVariant)) {
      setActiveVariant(hierarchy.variants[0] ?? 'cloud');
    }
  }, [hierarchy, activeVariant]);

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const tree = useMemo<DocVariantTree | null>(
    () => (hierarchy ? (hierarchy.trees[activeVariant] ?? null) : null),
    [hierarchy, activeVariant],
  );

  const allNodes = useMemo<DocNode[]>(
    () => (tree ? flattenNodes(tree.root?.children ?? []) : []),
    [tree],
  );

  const searchResults = useMemo<DocNode[] | null>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return allNodes.filter((n) => n.title.toLowerCase().includes(q));
  }, [allNodes, query]);

  const effectivePath = useMemo((): string | null => {
    if (!tree) return null;
    return selectedPath ?? firstSelectablePath(tree);
  }, [tree, selectedPath]);

  const curArtifact = useMemo(
    () => (effectivePath ? findArtifactForPath(artifacts, effectivePath) : null),
    [artifacts, effectivePath],
  );

  const handleSelect = useCallback((path: string) => {
    setSelectedPath(path);
    setQuery(''); // close search on selection
  }, []);

  const handleVariantChange = useCallback((v: string) => {
    setActiveVariant(v);
    setSelectedPath(null);
    setQuery('');
  }, []);

  // Fallback when hierarchy fails to parse
  if (!hierarchy || !tree) {
    const fallback = artifacts.find(
      (a) => !a.name.endsWith('docs-hierarchy.json') && !a.name.endsWith('screenshots.json'),
    );
    return (
      <div className="scroll" style={{ flex: 1, padding: '16px 18px' }}>
        {fallback ? (
          <ArtifactBlock artifact={fallback} imageIndex={imageIndex} />
        ) : (
          <div style={{ padding: 40, textAlign: 'center', opacity: 0.5, fontSize: 13 }}>
            No documentation content found
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

      {/* ── Left tree sidebar ── */}
      <div
        style={{
          width: sidebarWidth,
          flexShrink: 0,
          borderRight: '1px solid var(--line)',
          background: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* Variant tabs */}
        {hierarchy.variants.length > 1 && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
            {hierarchy.variants.map((v) => (
              <button
                key={v}
                onClick={() => handleVariantChange(v)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: activeVariant === v ? '2px solid var(--accent-2)' : '2px solid transparent',
                  borderRadius: 0,
                  padding: '8px 14px',
                  fontSize: 11,
                  fontWeight: activeVariant === v ? 600 : 400,
                  color: activeVariant === v ? 'var(--accent-2)' : 'inherit',
                  cursor: 'pointer',
                }}
              >
                {hierarchy.trees[v]?.label ?? v.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid var(--line)',
              borderRadius: 6,
              padding: '6px 10px',
              background: 'var(--bg-sunken)',
            }}
          >
            {/* Search icon */}
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, opacity: 0.4 }}>
              <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.3" />
              <path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search by title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: 12,
                flex: 1,
                color: 'inherit',
                fontFamily: 'inherit',
              }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  opacity: 0.4,
                  fontSize: 14,
                  lineHeight: 1,
                  color: 'inherit',
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Tree or search results */}
        <div className="scroll" style={{ flex: 1 }}>
          {searchResults !== null ? (
            /* Flat search results */
            <div style={{ padding: '6px 0' }}>
              {searchResults.length === 0 ? (
                <div style={{ padding: '16px 14px', fontSize: 12, opacity: 0.5 }}>
                  No results for "{query}"
                </div>
              ) : (
                searchResults.map((node) => (
                  <SearchResultRow
                    key={node.slug + '|' + node.path}
                    node={node}
                    isSelected={effectivePath === node.path}
                    onSelect={handleSelect}
                  />
                ))
              )}
            </div>
          ) : (
            /* Hierarchical tree */
            <div style={{ padding: '6px 0' }}>
              <DocTreeNodes
                nodes={tree.root.children ?? []}
                depth={0}
                selectedPath={effectivePath}
                onSelect={handleSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Content pane ── */}
      <div
        className="scroll"
        style={{
          flex: 1,
          minWidth: 0,
          padding: '20px 24px',
          background: 'var(--bg)',
        }}
      >
        {curArtifact ? (
          <ArtifactBlock artifact={curArtifact} imageIndex={imageIndex} />
        ) : effectivePath ? (
          <div style={{ padding: 40, textAlign: 'center', opacity: 0.4 }}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Page not yet generated</div>
            <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{effectivePath}</div>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', opacity: 0.4 }}>
            <div style={{ fontSize: 13 }}>Select a page from the tree</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Search result row ────────────────────────────────────────────────────────

function SearchResultRow({
  node,
  isSelected,
  onSelect,
}: {
  node: DocNode;
  isSelected: boolean;
  onSelect: (path: string) => void;
}) {
  const iconColor = isSelected ? 'var(--accent-2)' : 'var(--ink-muted)';
  return (
    <div
      onClick={() => onSelect(node.path)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '7px 14px 7px 12px',
        cursor: 'pointer',
        borderLeft: isSelected ? '3px solid var(--accent-2)' : '3px solid transparent',
        background: isSelected ? 'var(--accent-soft)' : 'transparent',
        color: isSelected ? 'var(--accent-2)' : 'inherit',
      }}
    >
      <PageIcon color={iconColor} />
      <span
        style={{
          fontSize: 13,
          fontWeight: isSelected ? 500 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={node.title}
      >
        {node.title}
      </span>
    </div>
  );
}

// ── Tree nodes ───────────────────────────────────────────────────────────────

function DocTreeNodes({
  nodes,
  depth,
  selectedPath,
  onSelect,
}: {
  nodes: DocNode[];
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const sorted = useMemo(
    () => [...nodes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [nodes],
  );
  return (
    <>
      {sorted.map((node) => (
        <DocTreeNode
          key={node.slug + '|' + node.path}
          node={node}
          depth={depth}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function DocTreeNode({
  node,
  depth,
  selectedPath,
  onSelect,
}: {
  node: DocNode;
  depth: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
}) {
  const isGroup = (node.children?.length ?? 0) > 0;
  const [open, setOpen] = useState(depth < 2);
  const isSelected = selectedPath === node.path;

  const iconColor = isSelected ? 'var(--accent-2)' : 'var(--ink-muted)';

  // Left indent: 12px base + 18px per depth level
  const indent = 12 + depth * 18;

  if (isGroup) {
    return (
      <div>
        {/* Group row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: `7px 12px 7px ${indent}px`,
            cursor: 'pointer',
            borderLeft: isSelected ? '3px solid var(--accent-2)' : '3px solid transparent',
            background: isSelected ? 'var(--accent-soft)' : 'transparent',
            color: isSelected ? 'var(--accent-2)' : 'inherit',
          }}
        >
          {/* Chevron — toggles open/closed, does NOT navigate */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            style={{
              width: 14,
              flexShrink: 0,
              fontSize: 10,
              color: isSelected ? 'var(--accent-2)' : 'var(--ink-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            {open ? '∨' : '>'}
          </span>

          {/* Page icon */}
          <PageIcon color={iconColor} />

          {/* Title — navigates to group index.md */}
          <span
            onClick={() => onSelect(node.path)}
            style={{
              flex: 1,
              fontSize: 13,
              fontWeight: isSelected ? 600 : 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
            title={node.title}
          >
            {node.emoji ? `${node.emoji} ` : ''}
            {node.title}
          </span>
        </div>

        {/* Children */}
        {open && (
          <DocTreeNodes
            nodes={node.children}
            depth={depth + 1}
            selectedPath={selectedPath}
            onSelect={onSelect}
          />
        )}
      </div>
    );
  }

  // Leaf node
  return (
    <div
      onClick={() => onSelect(node.path)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: `7px 12px 7px ${indent}px`,
        cursor: 'pointer',
        borderLeft: isSelected ? '3px solid var(--accent-2)' : '3px solid transparent',
        background: isSelected ? 'var(--accent-soft)' : 'transparent',
        color: isSelected ? 'var(--accent-2)' : 'inherit',
        userSelect: 'none',
      }}
    >
      {/* Bullet — aligns with the chevron width */}
      <span
        style={{
          width: 14,
          flexShrink: 0,
          textAlign: 'center',
          fontSize: 16,
          lineHeight: '14px',
          color: isSelected ? 'var(--accent-2)' : 'var(--ink-muted)',
        }}
      >
        •
      </span>

      {/* Page icon */}
      <PageIcon color={iconColor} />

      {/* Title */}
      <span
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: isSelected ? 500 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={node.title}
      >
        {node.title}
      </span>
    </div>
  );
}
